import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { filter, map, takeUntil } from 'rxjs/operators';
import { User } from '@models/models';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { TwoFaSetupModalComponent } from '@web-components/2fa/modals/two-fa-setup-modal/two-fa-setup-modal';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TwoFaAuthenticationDataService } from '@services/user/two-fa-authentication.data.service';
import { TwoFaLoginModalComponent } from '@web-components/2fa/modals/two-fa-login-modal/two-fa-login-modal';
import { TwoFaResetModalComponent } from '@web-components/2fa/modals/two-fa-reset-modal/two-fa-reset-modal';
import {
	LoginModalPayload,
	TwoFactorAuthorization,
	TwoFaModalControl
} from '@models/two-fa.models';
import { SubscriptionStatus } from '@models/subscription.model';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';

@Component({
	selector: 'two-fa-control',
	template: require('./two-fa-control.component.html'),
	styles: [require('./two-fa-control.component.scss')]
})
export class TwoFaControlComponent implements OnInit, OnDestroy {
	private twoFaSetupModalRef: NgbModalRef;
	private twoFaLoginModalRef: NgbModalRef;
	private twoFaResetModalRef: NgbModalRef;

	private twoFASettings: TwoFactorAuthorization;
	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private accountService: AccountServiceWebProvider,
		private modalService: SNModalService,
		private twoFactorAuthenticationService: TwoFaAuthenticationDataService,
		private subscriptionDataService: NewSubscriptionServiceDataProvider,
		private brandsDataService: BrandsDataServiceProvider
	) {}

	ngOnInit(): void {
		this.getUserTwoFASettings();
		this.listenToTwoFaModalControlTrigger();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	private getUserTwoFASettings(): void {
		this.accountService.userDataObservable
			.pipe(
				takeUntil(this.unsubscribe),
				filter(
					(user: User): boolean => user !== null && typeof user !== 'undefined'
				),
				map((user: User): TwoFactorAuthorization => user.twoFactorAuthorization)
			)
			.subscribe((settings: TwoFactorAuthorization): void => {
				this.twoFASettings = JSON.parse(JSON.stringify(settings));
			});
	}

	private listenToTwoFaModalControlTrigger(): void {
		this.twoFactorAuthenticationService.twoFaModalControlState
			.pipe(
				takeUntil(this.unsubscribe),
				filter((data: TwoFaModalControl): boolean => data && data.state)
			)
			.subscribe((data: TwoFaModalControl): void => {
				switch (data.action) {
					case 'SETUP':
						this.handleSetupModal(data.payload);
						break;
					case 'LOGIN':
						this.handleLoginModal(data.payload);
						break;
					case 'RESET':
						this.handleResetModal(data.payload);
						break;
					default:
						break;
				}
			});
	}

	private handleSetupModal(payload): void {
		this.twoFaSetupModalRef = this.openCorrectModal(TwoFaSetupModalComponent, {
			windowClass: 'ng-bootstrap-modal modal-visible modal-lowered-index',
			backdropClass: 'ng-bootstrap-backdrop modal-lowered-index'
		});

		if (payload && payload.user2faState) {
			this.twoFaSetupModalRef.componentInstance.userTwoFASettings =
				payload.user2faState;
			this.twoFaSetupModalRef.componentInstance.primaryAction =
				'SETUP_AND_LOGIN';
		} else {
			this.twoFaSetupModalRef.componentInstance.userTwoFASettings = this.twoFASettings.user2faState;
			this.twoFaSetupModalRef.componentInstance.primaryAction = 'SETUP_ONLY';
		}

		if (payload && payload.enforce) {
			this.twoFaSetupModalRef.componentInstance.enforce = payload.enforce;
		}

		this.twoFaSetupModalRef.result.then((state: string): void => {
			if (state === 'SETUP_AND_LOGIN' && (payload && payload.loginActions)) {
				this.logUserIn();
			}

			if (state === 'DISMISS_SETUP' && (payload && payload.loginActions)) {
				this.accountService.logoutWeb();
			}
		});
	}

	private handleLoginModal(payload: LoginModalPayload): void {
		this.twoFaLoginModalRef = this.openCorrectModal(TwoFaLoginModalComponent, {
			windowClass: 'ng-bootstrap-modal modal-visible modal-narrow'
		});
		this.twoFaLoginModalRef.componentInstance.userPayload = payload.userPayload;

		this.twoFaLoginModalRef.result.then((state: string): void => {
			if (state === 'LAUNCH_RESET') {
				this.twoFactorAuthenticationService.setTwoFaModalControlState({
					state: true,
					action: 'RESET',
					payload
				});
			}

			if (state === 'SUCCESS_LOGIN') {
				this.logUserIn();
			}
		});
	}

	private handleResetModal(payload: LoginModalPayload): void {
		this.twoFaResetModalRef = this.openCorrectModal(TwoFaResetModalComponent, {
			windowClass: 'ng-bootstrap-modal modal-visible modal-narrow'
		});
		this.twoFaResetModalRef.componentInstance.recoverUser2FA =
			payload.recoverUser2FA;
		this.twoFaResetModalRef.componentInstance.userPayload = payload.userPayload;

		this.twoFaResetModalRef.result.then((state: string): void => {
			if (state === 'LAUNCH_LOGIN') {
				this.twoFactorAuthenticationService.setTwoFaModalControlState({
					state: true,
					action: 'LOGIN',
					payload
				});
			}

			if (state === 'SUCCESS_LOGIN') {
				this.logUserIn();
			}
		});
	}

	private openCorrectModal(modalComponent, options?): NgbModalRef {
		return this.modalService.openModal(modalComponent, options || null);
	}

	private logUserIn(): void {
		this.subscriptionDataService
			.fetchSubscriptionStatus()
			.subscribe((subscription: SubscriptionStatus): void => {
				if (subscription.currentPlan) {
					this.brandsDataService.getBrandsData();
					this.accountService.getUserWeb().subscribe((): void => {
						this.subscriptionDataService.setSubscriptionData(subscription);

						if (subscription.loginActions) {
							this.subscriptionDataService.performAfterLoginActions(
								subscription.loginActions
							);
						}
						this.accountService.emitUserLoginAction(true);
					});
				} else {
					this.subscriptionDataService.setChoosePlanOpenModalStatus({
						state: true,
						payload: null
					});
				}
			});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/2fa/two-fa-control/two-fa-control.component.ts